import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, NgZone, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@merchant-portal/app/core/auth.service';
import { EnvFlagService } from '@merchant-portal/app/core/flag/services/env-flag.service';
import { UserAccessService } from '@merchant-portal/app/core/user-access/services/user-access.service';
import { dashboardFeature } from '@merchant-portal/app/features/dashboard/store/dashboard.reducer';
import {
  SidebarState,
  SidebarStateService,
} from '@merchant-portal/app/services/sidebar-state.service';
import { UtilService } from '@merchant-portal/app/services/util.service';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { IconType } from 'ui-component/src/lib/icons';
import { ISidebar } from '../../models/sidebar/sidebar.interface';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false,
})
export class SidebarComponent implements OnInit {
  listMenu: ISidebar[] = [];
  sidebarState$: Observable<SidebarState>;
  logoHovered = false;
  private hoverTimeout: any;

  treeControl = new NestedTreeControl<ISidebar>(node => node.children);
  dataSource = new MatTreeNestedDataSource<ISidebar>();

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private userAccessService: UserAccessService,
    private flagService: EnvFlagService,
    private store: Store,
    private util: UtilService,
    private authService: AuthService,
    private sidebarStateService: SidebarStateService
  ) {
    this.sidebarState$ = this.sidebarStateService.state$;
    this.router.events.pipe(takeUntilDestroyed()).subscribe(ev => {
      if (ev instanceof NavigationEnd) {
        this.checkActiveMenu();
      }
    });

    this.sidebarState$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.checkActiveMenu();
    });
  }

  ngOnInit(): void {
    this.getListMenu();
  }

  hasChild = (_: number, node: ISidebar) => !!node.children && node.children.length > 0;

  checkActiveMenu() {
    const currentRoute = this.router.url.split('?')[0].split('#')[0]; // Remove query params
    const currentSidebarState = this.sidebarStateService.currentState;
    const isMinimizedMode =
      currentSidebarState.isMinimized &&
      !currentSidebarState.isHovered &&
      !currentSidebarState.isSideBySide;

    this.treeControl.collapseAll();
    let prevChild: ISidebar | undefined = undefined;
    let activeMenuLabel = 'Home';
    let activeParentMenuLabel: string | undefined = undefined;

    this.listMenu.forEach(item => {
      item.isActive = false;
      let hasActiveChild = false;

      // Check children first (they are more specific)
      item.children?.forEach(child => {
        if (this.isRouteMatch(currentRoute, child.routerLink)) {
          child.isActive = true;
          hasActiveChild = true;
          this.treeControl.expand(item);
          activeMenuLabel = child.label;
          activeParentMenuLabel = item.label; // Store parent label

          if (prevChild) {
            prevChild.isActive = false;
          }
          prevChild = child;
        } else {
          child.isActive = false;
        }
      });

      // Then check parent
      if (!hasActiveChild && this.isRouteMatch(currentRoute, item.routerLink)) {
        item.isActive = true;
        activeMenuLabel = item.label;
        activeParentMenuLabel = undefined; // No parent for top-level items
      } else if (hasActiveChild) {
        // Parent selection based on sidebar mode
        item.isActive = isMinimizedMode;
      }
    });

    this.sidebarStateService.setActiveMenuLabel(activeMenuLabel, activeParentMenuLabel);
  }

  private isRouteMatch(currentRoute: string, menuRoute: string): boolean {
    if (!menuRoute || menuRoute === '') {
      return false;
    }

    if (currentRoute === menuRoute) {
      return true;
    }

    if (currentRoute.startsWith(menuRoute + '/')) {
      return true;
    }

    return false;
  }

  activateMenu(menu: ISidebar) {
    if (menu.routerLink) {
      this.navigateToMenu(menu.routerLink);
      this.resetActiveMenu();
      menu.isActive = true;
      if (
        this.sidebarStateService.currentState.isMobile &&
        this.sidebarStateService.currentState.isDrawerOpen
      ) {
        this.closeDrawer();
      }
    } else {
      this.treeControl.expand(menu);
    }
  }

  nodeClicked(node: ISidebar) {
    this.treeControl.collapseAll();
    this.treeControl.expand(node);
  }

  resetActiveMenu(menuList: ISidebar[] = this.listMenu) {
    menuList.forEach(item => {
      item.isActive = false;
      if (item.children) {
        this.resetActiveMenu(item.children);
      }
    });
  }

  navigateToMenu(url: string) {
    this.ngZone.run(() => {
      this.router.navigateByUrl(url);
    });
  }

  getListMenu() {
    this.store.select(dashboardFeature.selectMenuList).subscribe(menuList => {
      if (menuList) {
        this.listMenu = this.util.deepCopy(menuList);
        this.injectDashboardChildMenu();
        this.dataSource.data = this.listMenu;
        this.checkActiveMenu();
      }
    });
  }

  // Injects a "Dashboard" child menu for parent menus that serve as dashboards.
  // Parent menus with children only expand/collapse -- they don't navigate.
  // This adds a clickable child that navigates to the parent's path.
  injectDashboardChildMenu() {
    const dashboardMenus = [
      { labels: ['Disbursement', 'Local Payouts'], routerLink: '/disbursement', permission: 'disbursement.view' },
      { labels: ['Card Funded Payout'], routerLink: '/card-funded-payout', permission: 'card-funded-payout.view' },
    ];

    for (const config of dashboardMenus) {
      const index = this.listMenu.findIndex(menu => config.labels.includes(menu.label));
      if (index !== -1) {
        this.listMenu[index]?.children?.unshift({
          label: 'Dashboard',
          routerLink: config.routerLink,
          isActive: false,
          icon: undefined,
          customIcon: undefined,
          permissions: [config.permission],
        });
      }
    }
  }

  private readonly supportedIcons: Record<string, IconType> = {
    'wallet-sidebar.svg': 'Wallet',
    'credit-card-icon.svg': 'Payment',
    'platform.svg': 'Merchant',
    'credit-card-icon-clock.svg': 'Balance History',
    'xb.svg': 'International Payout',
    'send.svg': 'Payout',
  };

  getIconType(iconPathOrKey: string | undefined): IconType | null {
    if (!iconPathOrKey) return null;

    const filename = iconPathOrKey.split('/').pop()?.toLowerCase();
    return this.supportedIcons[filename || ''] || null;
  }

  onMenuMouseEnter(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    const currentState = this.sidebarStateService.currentState;
    if (currentState.isMinimized && !currentState.isSideBySide && !currentState.isMobile) {
      this.sidebarStateService.setHovered(true);
    }
  }

  onSidebarMouseLeave(): void {
    const currentState = this.sidebarStateService.currentState;
    if (!currentState.isMobile) {
      this.hoverTimeout = setTimeout(() => {
        this.sidebarStateService.setHovered(false);
      }, 100);
    }
  }

  toggleSideBySide(): void {
    // Clear logo hover state when toggling
    this.logoHovered = false;
    this.sidebarStateService.toggleSideBySide();
  }

  closeDrawer(): void {
    this.sidebarStateService.setDrawerOpen(false);
  }
}
